<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="xl"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
        >

            <template #modal-title>
                Import Translation JSON
            </template>

            <div class="mb-3">
                <b-form-file
                    v-model="json_file"
                    accept="application/json"
                    browse-text="Upload File"
                    @change="upload($event)"
                />

                <b-form-text>Choose translation JSON file to import.</b-form-text>
            </div>

            <content-placeholders v-if="uploading_file">
                <content-placeholders-text :lines="3" />
            </content-placeholders>
            <b-list-group class="mb-3">
                <b-list-group-item 
                    v-for="publication, index in json_data.units" 
                    :key="'weeks' + publication.id" 
                    :class="{'border-top': false, 'rounded-0': false}"
                >
                    <div class="h6 mb-0">
                        <span class="collapse-icon text-dark mr-1" v-b-toggle="'collapse-weeks-' + publication.id">
                            <span class="open">
                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                            </span>
                            <span class="closed">
                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                            </span>
                            {{publication.units.length}} week{{publication.units.length > 1 ? 's' : ''}} in {{publication.sku}}
                        </span>
                    </div>
                    <b-collapse :id="'collapse-weeks-' + publication.id">
                        <div class="mt-2">
                            <b-list-group flush>
                                <b-list-group-item v-for="unit in publication.units">
                                    Week {{unit.order_number}} - {{unit.title}}
                                </b-list-group-item>
                            </b-list-group>
                        </div>
                    </b-collapse>
                </b-list-group-item>

                <b-list-group-item 
                    v-for="publication, index in json_data.unit_questions" 
                    :key="'unit-questions' + publication.id" 
                    :class="{'border-top': false, 'rounded-0': false}"
                >
                    <div class="h6 mb-0">
                        <span class="collapse-icon text-dark mr-1" v-b-toggle="'collapse-unit-questions-' + publication.id">
                            <span class="open">
                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                            </span>
                            <span class="closed">
                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                            </span>
                            {{assessmentQuestionCount(publication.sku)}} 
                            assessment question{{assessmentQuestionCount(publication.sku) > 1 ? 's' : ''}} in {{publication.sku}}
                        </span>
                    </div>
                    <b-collapse :id="'collapse-unit-questions-' + publication.id">
                        <div class="mt-2">
                            <b-list-group flush>
                                <template v-for="unit in publication.units">
                                    <b-list-group-item v-for="question in unit.questions">
                                        Week {{unit.order_number}} / {{clamp(question.content.question || question.content.question_prompt, 50)}}
                                    </b-list-group-item>
                                </template>
                            </b-list-group>
                        </div>
                    </b-collapse>
                </b-list-group-item>

                <b-list-group-item 
                    v-for="publication, index in json_data.articles" 
                    :key="'articles' + publication.id" 
                    :class="{'border-top': false, 'rounded-0': false}"
                >
                    <div class="h6 mb-0">
                        <span class="collapse-icon text-dark mr-1" v-b-toggle="'collapse-articles-' + publication.id">
                            <span class="open">
                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                            </span>
                            <span class="closed">
                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                            </span>
                            {{articleCount(publication.sku)}} article{{articleCount(publication.sku) > 1 ? 's' : ''}} in {{publication.sku}}
                        </span>
                    </div>
                    <b-collapse :id="'collapse-articles-' + publication.id">
                        <div class="mt-2">
                            <b-list-group flush>
                                <template v-for="unit in publication.units">
                                    <b-list-group-item v-for="article in unit.articles">
                                        Week {{unit.order_number}} / {{article.title}}
                                    </b-list-group-item>
                                </template>
                            </b-list-group>
                        </div>
                    </b-collapse>
                </b-list-group-item>

                <b-list-group-item 
                    v-for="publication, index in json_data.article_questions" 
                    :key="'article-questions' + publication.id" 
                    :class="{'border-top': false, 'rounded-0': false}"
                >
                    <div class="h6 mb-0">
                        <span class="collapse-icon text-dark mr-1" v-b-toggle="'collapse-article-questions-' + publication.id">
                            <span class="open">
                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                            </span>
                            <span class="closed">
                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                            </span>
                            {{articleQuestionCount(publication.sku)}} article question{{articleQuestionCount(publication.sku) > 1 ? 's' : ''}} in {{publication.sku}}
                        </span>
                    </div>
                    <b-collapse :id="'collapse-article-questions-' + publication.id">
                        <div class="mt-2">
                            <b-list-group flush>
                                <template v-for="unit in publication.units">
                                    <template v-for="article in unit.articles">
                                        <b-list-group-item v-for="question in article.questions">
                                            {{article.title}} / {{clamp(question.content.question || question.content.question_prompt, 50)}}
                                        </b-list-group-item>
                                    </template>
                                </template>
                            </b-list-group>
                        </div>
                    </b-collapse>
                </b-list-group-item>
            </b-list-group>

            <template #modal-footer>
                <b-button variant="light" @click="cancel">
                    Cancel
                </b-button>
                <b-button variant="secondary" @click="importJson" v-if="json_file">
                    <font-awesome-icon :icon="['fad', 'hockey-mask']" fixed-width /> Import JSON
                </b-button>
            </template>

        </b-modal>
    </div>
</template>

<style lang="scss" scoped>

</style>

<script>
import { mapState } from 'vuex'

export default {
    data: () => ({
        uploading_file: false,
        json_data: {},
        json_file: null
    }),
    props: [
        'value',
        'publication'
    ],
    components: {},
    computed: {
        ...mapState({
            // validating: state => state.utilities.validating
        }),
        modal_id () {
            let modal_id = 'import-translation-json-modal'
            return modal_id
        }
    },
    methods: {
        articleCount(sku) {
            let articles = {}
            _.each(this.json_data.articles, publication => {
                articles[publication.sku] = 0;
                _.each(publication.units, unit => {
                    articles[publication.sku] += unit.articles.length
                })
            })
            return articles[sku]
        },
        assessmentQuestionCount(sku) {
            let questions = {}
            _.each(this.json_data.unit_questions, publication => {
                questions[publication.sku] = 0;
                _.each(publication.units, unit => {
                    questions[publication.sku] += unit.questions.length
                })
            })
            return questions[sku]
        },
        articleQuestionCount(sku) {
            let questions = {}
            _.each(this.json_data.article_questions, publication => {
                questions[publication.sku] = 0;
                _.each(publication.units, unit => {
                    _.each(unit.articles, article => {
                        questions[publication.sku] += article.questions.length
                    })
                })
            })
            return questions[sku]
        },
        upload (event) {
            let file = null
            let form_data = new FormData()

            if (event.target && event.target.files) {
                file = event.target.files[0]
                form_data.append('import_json', file)
                this.uploading_file = true
                this.axios.post('/api/publications/upload_json', form_data)
                .then(response => {
                    this.json_data = response.data
                    this.uploading_file = false
                })
                .catch(error => {
                    if (error) {
                        this.uploading_file = false
                        this.json_file = null
                    }
                })
            }
        },
        importJson() {
            let file = null
            let form_data = new FormData()

            if (this.json_file) {
                file = this.json_file
                form_data.append('import_json', file)
                this.uploading_file = true
                this.json_data = {}

                this.axios.post('/api/publications/import_json', form_data)
                .then(response => {
                    this.uploading_file = false
                    this.json_file = null
                    this.hide()
                    this.$bvToast.toast('JSON imported.', {
                        title: 'Success',
                        variant: 'success',
                        solid: false,
                        autoHideDelay: 5000,
                        appendToast: true
                    })
                })
                .catch(error => {
                    if (error) {
                        this.uploading_file = false
                        this.json_file = null
                    }
                })
            }
        },
        clamp (text, length) {
            if(text == '' || !text) return ''
            var clamp = '...';
            var node = document.createElement('div');
            node.innerHTML = text;
            var content = node.textContent;
            return content.length > length ? content.slice(0, length) + clamp : content;
        },
        cancel (event) {
            event.preventDefault()

            this.hide()
        },
        hide () {
            this.$emit('hide')
        },
        hidden () {
            this.$store.commit('setValidating', false)

            this.$emit('hide')
        }
    }
}
</script>
